import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchCountries, fetchStates, type CountryOption, type StateOption } from "@/lib/geo/geoStore";
import {
  DEFAULT_SUPPLIER_TYPE,
  normalizeSupplierType,
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_TYPE_VALUES,
  type SupplierDraft,
  type SupplierRecord,
} from "@/lib/suppliers/suppliersStore";
import { useEffect, useState } from "react";
import { getStoredAuthUser } from "@/lib/authUser";
import { formatWithMask } from "@/lib/utils";

type Props = {
  draft: SupplierDraft;
  onChange: (draft: SupplierDraft) => void;
  disabled?: boolean;
};

export function SupplierFormFields({ draft, onChange, disabled }: Props) {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);

  const user = getStoredAuthUser();
  const mask = user?.company_branding?.cnic_masking;
  const placeholder = mask ? mask.replace(/9/g, "x") : "National ID / CNIC";
  const maxLength = mask ? mask.length : 32;

  const update = <K extends keyof SupplierDraft>(key: K, value: SupplierDraft[K]) => {
    onChange({ ...draft, [key]: value });
  };

  useEffect(() => {
    let mounted = true;
    fetchCountries()
      .then((rows) => {
        if (mounted) setCountries(rows);
      })
      .catch(() => {
        if (mounted) setCountries([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Default to the only/first country (Pakistan) when none is selected yet.
  useEffect(() => {
    if (draft.country == null && countries.length > 0) {
      onChange({ ...draft, country: countries[0].id, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, draft.country]);

  useEffect(() => {
    let mounted = true;
    if (!draft.country) {
      setStates([]);
      return;
    }
    setStatesLoading(true);
    fetchStates(draft.country)
      .then((rows) => {
        if (mounted) setStates(rows);
      })
      .catch(() => {
        if (mounted) setStates([]);
      })
      .finally(() => {
        if (mounted) setStatesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [draft.country]);

  const handleCountryChange = (value: string) => {
    onChange({ ...draft, country: Number(value), state: null });
  };

  const handleStateChange = (value: string) => {
    onChange({ ...draft, state: Number(value) });
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          Supplier name <span className="text-destructive">*</span>
        </Label>
        <Input
          className="glass-input h-10"
          value={draft.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="e.g. Ali Motors"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          Father name
        </Label>
        <Input
          className="glass-input h-10"
          value={draft.fatherName}
          onChange={(e) => update("fatherName", e.target.value)}
          placeholder="e.g. Muhammad Khan"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          CNIC #
        </Label>
        <Input
          className="glass-input h-10"
          value={draft.cnic}
          onChange={(e) => update("cnic", mask ? formatWithMask(e.target.value, mask) : e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          Contact Number <span className="text-destructive">*</span>
        </Label>
        <Input
          className="glass-input h-10"
          type="tel"
          value={draft.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="+92 300 1234567"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Contact person</Label>
        <Input
          className="glass-input h-10"
          value={draft.contactPerson}
          onChange={(e) => update("contactPerson", e.target.value)}
          placeholder="e.g. Ali Hassan"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Email</Label>
        <Input
          className="glass-input h-10"
          type="email"
          value={draft.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="email@example.com"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          Country <span className="text-destructive">*</span>
        </Label>
        <Select
          value={draft.country != null ? String(draft.country) : ""}
          onValueChange={handleCountryChange}
          disabled={disabled || countries.length === 0}
        >
          <SelectTrigger className="glass-input h-10">
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          State <span className="text-destructive">*</span>
        </Label>
        <Select
          value={draft.state != null ? String(draft.state) : ""}
          onValueChange={handleStateChange}
          disabled={disabled || !draft.country || statesLoading || states.length === 0}
        >
          <SelectTrigger className="glass-input h-10">
            <SelectValue placeholder={statesLoading ? "Loading states…" : "Select state"} />
          </SelectTrigger>
          <SelectContent>
            {states.map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          City <span className="text-destructive">*</span>
        </Label>
        <Input
          className="glass-input h-10"
          value={draft.city}
          onChange={(e) => update("city", e.target.value)}
          placeholder="e.g. Lahore"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Postal Code</Label>
        <Input
          className="glass-input h-10"
          value={draft.postalCode}
          onChange={(e) => update("postalCode", e.target.value)}
          placeholder="e.g. 54000"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Address Line 1</Label>
        <Input
          className="glass-input h-10"
          value={draft.addressLine1}
          onChange={(e) => update("addressLine1", e.target.value)}
          placeholder="House / street / area"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Address Line 2</Label>
        <Input
          className="glass-input h-10"
          value={draft.addressLine2}
          onChange={(e) => update("addressLine2", e.target.value)}
          placeholder="Apartment, suite, landmark (optional)"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Type</Label>
        <Select
          value={draft.type ?? DEFAULT_SUPPLIER_TYPE}
          onValueChange={(v) => update("type", normalizeSupplierType(v))}
          disabled={disabled}
        >
          <SelectTrigger className="glass-input h-10">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {SUPPLIER_TYPE_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {SUPPLIER_TYPE_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Status</Label>
        <Select
          value={draft.status}
          onValueChange={(v) => update("status", v as SupplierRecord["status"])}
          disabled={disabled}
        >
          <SelectTrigger className="glass-input h-10">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
